import { useCallback } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';

import { useAppSelector, useAppDispatch } from 'store/hooks/useStore';
import {
  registrationActions,
  registrationSelectors,
} from 'store/reducers/releaseReducers/releaseComponentReducer/registrationSlice';
import * as releaseApi from 'api/releaseApi';
import { REGISTRATION_STEP } from 'constants/registrationStep';
import { useToast } from 'hooks/useToast';
import { loaderActions } from 'store/reducers/loaderReducer/loaderSlice';
import { ReleaseComponent, ReleaseComponentApi } from 'types/releaseComponent';
import { useListStore } from './useListStore';
import { EditHookReturnType } from '../types';

/**
 * コンポーネント登録 store の実装をコンポーネントから隠すための中間層
 */
export const useRegistrationStore = (): EditHookReturnType => {
  const dispatch = useAppDispatch();
  const { fetchReleaseComponents } = useListStore();
  const toast = useToast();
  const form = useForm<ReleaseComponent>({
    mode: 'all',
    defaultValues: {
      componentName: '',
      repositoryUrl: '',
      manifestName: '',
      targetKeys: [{ value: '' }],
    },
  });

  const { getValues, reset, handleSubmit, control, watch } = form;

  const { fields, append, remove } = useFieldArray({
    name: 'targetKeys',
    control,
  });
  const watchFieldArray = watch('targetKeys');
  const controlledFields = fields.map((field, index) => ({
    ...field,
    ...watchFieldArray[index],
  }));

  const onAppend = () => {
    append({ value: '' });
  };

  const onRemove = (index: number) => {
    remove(index);
  };

  const currentModalStep = useAppSelector(
    registrationSelectors.currentModalStep,
  );

  const isOpenModal = useAppSelector(registrationSelectors.isOpenModal);

  const onOpenModal = useCallback(() => {
    dispatch(registrationActions.modalOpened());
  }, [dispatch]);

  const onCloseModal = () => {
    dispatch(registrationActions.initialize());
    reset();
  };

  const onCloseModalWhenCompleted = () => {
    onCloseModal();
    void fetchReleaseComponents();
  };

  const onConfirm = () => {
    dispatch(registrationActions.updateStep(REGISTRATION_STEP.CONFIRMATION));
  };

  const onGoBack = () => {
    dispatch(registrationActions.updateStep(REGISTRATION_STEP.INPUT));
  };

  const onSubmit = handleSubmit(
    async (values: ReleaseComponent) => {
      dispatch(loaderActions.setLoading(true));
      // api送信用にデータ修正
      const targetKeys: string[] = [];
      values.targetKeys.forEach((targetKey) => {
        targetKeys.push(targetKey.value);
      });
      const ApiValues: ReleaseComponentApi = {
        componentName: values.componentName,
        repositoryUrl: values.repositoryUrl,
        manifestName: values.manifestName,
        targetKeys,
      };
      const result = await releaseApi.addComponent(ApiValues);
      if (result.ok) {
        dispatch(registrationActions.updateStep(REGISTRATION_STEP.RESULT));
      } else {
        toast({
          status: 'error',
          description: `コンポーネントの登録に失敗しました: ${result.err.message}`,
        });
      }
      dispatch(loaderActions.setLoading(false));
    },
    () => {
      toast({
        status: 'error',
        description: 'コンポーネントの登録に失敗しました。',
      });
    },
  );

  return {
    mode: '登録',
    isOpenModal,
    currentModalStep,
    onOpenModal,
    onCloseModal,
    onCloseModalWhenCompleted,
    onConfirm,
    onGoBack,
    onSubmit,
    onOpenModalDelete: () => null,
    form,
    values: getValues(),
    onAppend,
    onRemove,
    controlledFields,
  };
};
